import React, { useCallback, useMemo, useState } from 'react';

import { EyeOutlined, SearchOutlined } from '@ant-design/icons';
import {
  ButtonAction,
  ButtonView,
} from '@fastlessjs/backoffice-layer/dist/components/atoms';
import { PageProps } from '@fastlessjs/backoffice-layer/dist/components/pages/types';
import {
  currentEnterpriseId,
  useMultiTenancy,
} from '@fastlessjs/backoffice-layer/dist/hooks';
import debug from '@fastlessjs/backoffice-layer/dist/utils/debug';
import { Button, Input, Modal, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { format, isValid, parseISO } from 'date-fns';
import { navigate } from 'hookrouter-react';
import { APIOptions } from 'types';

import { t } from '../../config/translations';
import { ProcessingStatusTag } from '../components/ProcessingStatusTag';
import { ReturnStatusTag } from '../components/ReturnStatusTag';
import { useReturnsByOrder } from '../hooks';
import { Trace } from '../types';
import {
  getTraceDetailNavigatePath,
  getTraceListItemPath,
} from '../utils/traceNavigation';

import './SearchSuiviModal.less';

debug.registerComponent('SearchSuiviModal');

const MIN_ORDER_ID_LENGTH = 5;

const formatTrackingDate = (value?: string): string => {
  if (!value) {
    return '—';
  }
  try {
    const parsed = parseISO(value);
    if (!isValid(parsed)) {
      return value;
    }
    return format(parsed, 'dd/MM/yyyy HH:mm');
  } catch {
    return value;
  }
};

const SearchSuiviModal = ({
  org,
  enterpriseId: enterpriseIdProp,
}: PageProps & { enterpriseId?: string | number }) => {
  debug.trackRender('SearchSuiviModal');
  debug.log('SearchSuiviModal', `Rendering with org: ${org}`);
  const { getPage } = useMultiTenancy();
  const globalEnterpriseId = useMultiTenancy((state) => state.enterpriseId);
  const enterpriseId =
    enterpriseIdProp ??
    getPage('enterprises')?.[org ?? '']?.enterpriseId ??
    globalEnterpriseId ??
    currentEnterpriseId();
  const { Text } = Typography;

  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<APIOptions>({ orderId: undefined });
  const [searchAttemptedWithoutTenant, setSearchAttemptedWithoutTenant] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data, isLoading, isFetched, refetch, isFetching } = useReturnsByOrder(options);

  const hasClientContext = Boolean(enterpriseId);

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setInputValue('');
    setOptions({ orderId: undefined });
    setSearchAttemptedWithoutTenant(false);
    setIsModalOpen(false);
  }, []);

  const handleOpenTrace = useCallback(
    (record: Trace) => {
      if (!record.suiviId) {
        return;
      }
      const path = getTraceDetailNavigatePath(record);
      debug.log('SearchSuiviModal', `Opening trace detail: ${path}`);
      handleCloseModal();
      navigate(path);
    },
    [handleCloseModal],
  );

  const handleSearchReturn = useCallback(() => {
    const id = inputValue.trim();
    if (id.length <= MIN_ORDER_ID_LENGTH) {
      return;
    }

    const clientId = enterpriseId ? String(enterpriseId) : undefined;
    if (!clientId) {
      setSearchAttemptedWithoutTenant(true);
      debug.error('SearchSuiviModal', 'Missing enterpriseId — search aborted');
      return;
    }

    setSearchAttemptedWithoutTenant(false);
    debug.log(
      'SearchSuiviModal',
      `Searching return for order: ${id}, client: ${clientId}`,
    );

    const nextOptions = {
      orderId: id,
      clientId,
      tenantId: clientId,
    };

    const isSameSearch =
      options.orderId === nextOptions.orderId &&
      options.clientId === nextOptions.clientId;

    setOptions(nextOptions);

    if (isSameSearch) {
      refetch();
    }
  }, [enterpriseId, inputValue, options.clientId, options.orderId, refetch]);

  const showNotFound = isFetched && !isLoading && !isFetching && options.orderId && !data;
  const hasSingleResult = data?.rowCount === 1;
  const hasMultipleResults = (data?.rowCount ?? 0) > 1;
  const singleResult = hasSingleResult ? data?.rows[0] : undefined;
  const isSearching = isLoading || isFetching;
  const canSearch = inputValue.trim().length > MIN_ORDER_ID_LENGTH;

  const searchButton = (
    <Button
      key="search"
      htmlType="button"
      type="primary"
      icon={<SearchOutlined />}
      onClick={handleSearchReturn}
      loading={isSearching}
      disabled={!canSearch || !hasClientContext}
    >
      {t('searchModal.search')}
    </Button>
  );

  const openDetailsButton = singleResult ? (
    <Button
      key="open-details"
      htmlType="button"
      type="default"
      icon={<EyeOutlined />}
      style={{
        backgroundColor: 'var(--color-success)',
        borderColor: 'var(--color-success)',
        color: 'white',
      }}
      data-navigate-path={getTraceListItemPath(singleResult)}
      onClick={() => handleOpenTrace(singleResult)}
    >
      {t('searchModal.openDetails')}
    </Button>
  ) : (
    searchButton
  );

  const resultColumns = useMemo<ColumnsType<Trace>>(
    () => [
      {
        title: t('schema.trackingRef'),
        dataIndex: 'suiviId',
        key: 'suiviId',
      },
      {
        title: t('schema.returnStatus'),
        key: 'returnStatus',
        render: (_, record) => (
          <ReturnStatusTag status={record.displayReturnStatus || record.returnStatus} />
        ),
      },
      {
        title: t('schema.processingStatus'),
        key: 'processingStatus',
        render: (_, record) => (
          <ProcessingStatusTag status={record.displayProcessingStatus} />
        ),
      },
      {
        title: t('schema.trackingDate'),
        key: 'createdAt',
        // eslint-disable-next-line no-underscore-dangle -- DynamoDB audit field on Trace
        render: (_, record) => formatTrackingDate(record._createdAt),
      },
      {
        title: t('schema.name'),
        dataIndex: 'clientUserName',
        key: 'clientUserName',
        render: (value?: string) => value || '—',
      },
      {
        key: 'actions',
        width: 72,
        align: 'right',
        render: (_, record) => (
          <ButtonView
            dataNavigatePath={getTraceListItemPath(record)}
            onClick={() => handleOpenTrace(record)}
          />
        ),
      },
    ],
    [handleOpenTrace],
  );

  return (
    <>
      <ButtonAction type="primary" onClick={handleOpenModal}>
        {t('searchModal.searchReturn')}
      </ButtonAction>
      <Modal
        title={t('searchModal.searchAReturn')}
        open={isModalOpen}
        onCancel={handleCloseModal}
        width={hasMultipleResults ? 920 : 520}
        wrapClassName="search-suivi-modal"
        bodyStyle={{ padding: '12px 24px' }}
        footer={hasMultipleResults ? null : [openDetailsButton]}
      >
        <div className="search-suivi-modal__body">
          <div className="search-suivi-modal__field">
            <Text>{t('searchModal.enterOrderNumberForReturn')}</Text>
          </div>
          <div className="search-suivi-modal__field">
            <Input
              placeholder={t('searchModal.orderPlaceholder')}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onPressEnter={handleSearchReturn}
            />
          </div>
          {hasMultipleResults ? (
            <>
              <Text type="secondary" className="search-suivi-modal__hint">
                {t('searchModal.multipleReturnsFound').replace(
                  '{{count}}',
                  String(data?.rowCount ?? 0),
                )}
              </Text>
              <div className="search-suivi-modal__table-scroll">
                <Table<Trace>
                  size="small"
                  rowKey="suiviId"
                  columns={resultColumns}
                  dataSource={data?.rows ?? []}
                  pagination={false}
                />
              </div>
              <div className="search-suivi-modal__actions">{searchButton}</div>
            </>
          ) : null}
          {searchAttemptedWithoutTenant && !hasClientContext ? (
            <Text type="danger">{t('searchModal.missingEnterprise')}</Text>
          ) : null}
          {showNotFound ? (
            <Text type="danger">{t('searchModal.returnNotFound')}</Text>
          ) : null}
        </div>
      </Modal>
    </>
  );
};

export default SearchSuiviModal;

debug.markInitialized('SearchSuiviModal');
